// Detection Summary — split into narrative summary + assessment details accordions.

import React from "react";
import { Box, Stack, Typography, Chip } from "@mui/material";
import { ShieldCheck, Crosshair } from "lucide-react";

import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import StoryPanel from "./StoryPanel";
import {
  TEXT_SECONDARY,
  drawerActionChipSx,
  drawerPrimaryChipSx,
  drawerNeutralChipSx,
  drawerSeverityPalette,
} from "../wafEventDrawerShared";
import { actionLabel, formatCweMitreOwasp } from "../../../../../utils/wafEventV3";
import {
  spSumFlagRowSx,
  spSumFlagBlockSx,
  spSumFlagStageSx,
  spSeverityChipSx,
  spLh,
} from "./smartParseDrawerUi";

function ActionChip({ action }) {
  if (!action) return null;
  return (
    <Chip size="small" label={actionLabel(action)} sx={drawerActionChipSx(action)} />
  );
}

function SeverityChip({ severity }) {
  if (!severity) return null;
  const palette = drawerSeverityPalette(severity);
  return (
    <Chip size="small" label={palette.label} sx={spSeverityChipSx(severity)} />
  );
}

function resolveStatusChipContext(event) {
  const { assessment, primary_intent, intents } = event || {};
  if (!primary_intent) {
    return { assessment, primary_intent: null, classification: null };
  }

  const matchedIntent = Array.isArray(intents)
    ? intents.find(
      (intent) =>
        (primary_intent.intent_id && intent.intent_id === primary_intent.intent_id)
        || (primary_intent.intent_name && intent.intent_name === primary_intent.intent_name),
    )
    : null;

  return {
    assessment,
    primary_intent,
    classification: matchedIntent?.classification || primary_intent.classification,
  };
}

function StatusChips({ assessment, primary_intent, classification }) {
  const labels = formatCweMitreOwasp(classification);
  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      spacing={1.5}
      sx={{ gap: 1 }}
    >
      <ActionChip action={assessment?.action} />
      <SeverityChip severity={assessment?.severity} />
      {primary_intent?.intent_name && (
        <Chip size="small" label={primary_intent.intent_name} sx={drawerPrimaryChipSx} />
      )}
      {labels.map((label) => (
        <Chip key={label} size="small" label={label} sx={drawerNeutralChipSx} />
      ))}
    </Stack>
  );
}

/** Intent + CWE/MITRE/OWASP tags only (no action/severity). */
export function EventStatusTagRow({ event }) {
  const ctx = resolveStatusChipContext(event);
  const labels = formatCweMitreOwasp(ctx.classification);

  if (!ctx.primary_intent?.intent_name && labels.length === 0) return null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ gap: "7px" }}
    >
      {ctx.primary_intent?.intent_name && (
        <Chip
          size="small"
          icon={
            <LayerdIcon
              icon={DETECTION_ICONS.match}
              size={12}
              color="primary"
              style={{ marginLeft: 4 }}
            />
          }
          label={ctx.primary_intent.intent_name}
          sx={{
            ...drawerPrimaryChipSx,
            pl: 0.5,
            "& .MuiChip-icon": { ml: "6px" },
          }}
        />
      )}
      {labels.map((label) => (
        <Chip key={label} size="small" label={label} sx={drawerNeutralChipSx} />
      ))}
    </Stack>
  );
}

/** Status chips row — Blocked, severity, intent, CWE/MITRE. */
export function EventStatusChips({ event }) {
  const ctx = resolveStatusChipContext(event);
  return <StatusChips {...ctx} />;
}

/** Action + attack-stage flags below the headline in Detection Summary. */
export function DetectionSummarySectionChips({ assessment }) {
  if (!assessment) return null;

  const actionKey = String(assessment.action || "").toLowerCase();
  const isBlocked = actionKey === "block" || actionKey === "blocked";

  return (
    <Box sx={spSumFlagRowSx}>
      {assessment.action && (
        <Box component="span" sx={isBlocked ? spSumFlagBlockSx : spSumFlagStageSx}>
          <ShieldCheck size={12} strokeWidth={2.5} aria-hidden />
          {actionLabel(assessment.action)}
        </Box>
      )}
      {assessment.attack_stage && (
        <Box component="span" sx={spSumFlagStageSx}>
          <Crosshair size={12} strokeWidth={2.5} aria-hidden />
          {assessment.attack_stage} stage
        </Box>
      )}
    </Box>
  );
}

/** Headline, flags, narrative, objective, and why-blocked list. */
export function DetectionSummaryStory({ event }) {
  const { assessment, story } = event || {};

  return (
    <StoryPanel
      story={story}
      assessment={assessment}
      event={event}
      embedded
      hideKillChain
      headerSlot={<DetectionSummarySectionChips assessment={assessment} />}
    />
  );
}
